import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { Button } from "@/components/ui/button";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useCompare } from "@/contexts/CompareContext";
import {
  useDiscoveryInfluencers,
  useDiscoveryFilters,
  toCompareInfluencer,
  DiscoveryFilters,
  InfluencerCard,
  InfluencerCardSkeleton,
  InfluencerListView,
  ViewToggle,
  ViewMode,
  CompareTray,
  CompareModal,
} from "@/domains/discovery";

const LIMIT = 50;

export default function DiscoveryPageMain() {
  const navigate = useNavigate();
  const { isSelected, addToCompare, removeFromCompare, canAddMore } = useCompare();
  const filters = useDiscoveryFilters();
  const debouncedSearch = useDebouncedValue(filters.search, 400);

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem("discovery-view-mode") as ViewMode) || "grid";
    }
    return "grid";
  });

  const [offset, setOffset] = useState(0);
  const [allInfluencers, setAllInfluencers] = useState<any[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { data, isLoading, error, isFetching } = useDiscoveryInfluencers({
    searchTerm: debouncedSearch || filters.selectedQuick,
    platforms: filters.selectedPlatforms,
    location: filters.location,
    category: filters.category,
    language: filters.language,
    minFollowers: filters.minFollowers,
    maxFollowers: filters.maxFollowers,
    minEngagement: filters.minEngagement,
    maxEngagement: filters.maxEngagement,
    gender: filters.gender,
    limit: LIMIT,
    offset,
  });

  const totalCount = data?.totalCount || 0;
  const hasMore = allInfluencers.length < totalCount;

  // Reset pagination when filters change
  useEffect(() => {
    setOffset(0);
    setAllInfluencers([]);
  }, [
    debouncedSearch,
    filters.selectedQuick,
    filters.selectedPlatforms,
    filters.location,
    filters.category,
    filters.language,
    filters.minFollowers,
    filters.maxFollowers,
    filters.minEngagement,
    filters.maxEngagement,
    filters.gender,
  ]);

  // Accumulate results
  useEffect(() => {
    if (data?.influencers) {
      if (offset === 0) {
        setAllInfluencers(data.influencers);
      } else {
        setAllInfluencers((prev) => [...prev, ...data.influencers]);
      }
      setIsLoadingMore(false);
    }
  }, [data, offset]);

  const handleLoadMore = useCallback(() => {
    setIsLoadingMore(true);
    setOffset((prev) => prev + LIMIT);
  }, []);

  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem("discovery-view-mode", mode);
  };

  const handleToggleCompare = (inf: any) => {
    if (isSelected(inf.id)) {
      removeFromCompare(inf.id);
    } else {
      addToCompare(toCompareInfluencer(inf));
    }
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat bg-fixed select-none"
      style={{ backgroundImage: "url('/discovery-bg.png')" }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="max-w-screen-2xl mx-auto px-4 py-8 space-y-8">
        {/* Header + filters */}
        <div>
          <h1 className="text-3xl font-bold mb-2 font-tilt">Influencer Discovery</h1>
          <p className="text-black text-[18px] font-medium mb-4 font-raleway">
            Find creators that actually convert for your campaigns
          </p>
          <DiscoveryFilters
            search={filters.search}
            setSearch={filters.setSearch}
            selectedQuick={filters.selectedQuick}
            setSelectedQuick={filters.setSelectedQuick}
            selectedPlatforms={filters.selectedPlatforms}
            setSelectedPlatforms={filters.setSelectedPlatforms}
            location={filters.location}
            setLocation={filters.setLocation}
            category={filters.category}
            setCategory={filters.setCategory}
            language={filters.language}
            setLanguage={filters.setLanguage}
            minFollowers={filters.minFollowers}
            setMinFollowers={filters.setMinFollowers}
            maxFollowers={filters.maxFollowers}
            setMaxFollowers={filters.setMaxFollowers}
            selectedTier={filters.selectedTier}
            setSelectedTier={filters.setSelectedTier}
            minEngagement={filters.minEngagement}
            setMinEngagement={filters.setMinEngagement}
            maxEngagement={filters.maxEngagement}
            setMaxEngagement={filters.setMaxEngagement}
            selectedEngagementRanges={filters.selectedEngagementRanges}
            setSelectedEngagementRanges={filters.setSelectedEngagementRanges}
            gender={filters.gender}
            setGender={filters.setGender}
            onClear={filters.clearAll}
          />
        </div>

        {/* Results */}
        <div>
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-muted-foreground">
              {!isLoading && !error && (
                <span>
                  Showing{" "}
                  <span className="font-medium text-foreground">{allInfluencers.length}</span> of{" "}
                  <span className="font-medium text-foreground">{totalCount}</span> creators
                </span>
              )}
            </div>
            <ViewToggle view={viewMode} onViewChange={handleViewChange} />
          </div>

          {/* Search warnings */}
          {data?.searchWarnings?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg mb-4 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                {data.searchWarnings.map((warning: string, i: number) => (
                  <div key={i}>{warning}</div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg mb-4">
              <p className="font-semibold">Error loading influencers</p>
              <p className="text-sm">{error.message}</p>
            </div>
          )}

          {/* Loading skeletons */}
          {isLoading && (
            <>
              <div className="text-gray-500 text-xs mb-3">Loading creators...</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {Array.from({ length: 8 }).map((_, n) => (
                  <InfluencerCardSkeleton key={n} />
                ))}
              </div>
            </>
          )}

          {/* Results */}
          {!isLoading && !error && (
            <>
              {allInfluencers.length === 0 ? (
                <div className="text-center py-12 bg-muted/30 rounded-lg border border-border">
                  <p className="text-foreground font-medium mb-1">No creators found</p>
                  <p className="text-sm text-muted-foreground">Try adjusting your filters</p>
                </div>
              ) : (
                <>
                  {viewMode === "grid" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                      {allInfluencers.map((inf) => (
                        <InfluencerCard
                          key={inf.id}
                          inf={inf}
                          onView={(handle) => navigate(`/discovery/${handle.replace("@", "")}`)}
                          isSelected={isSelected(inf.id)}
                          onToggleCompare={() => handleToggleCompare(inf)}
                          canAddMore={canAddMore}
                        />
                      ))}
                    </div>
                  )}

                  {viewMode === "list" && (
                    <InfluencerListView
                      influencers={allInfluencers}
                      onInfluencerClick={(inf) =>
                        navigate(`/discovery/${inf.handle.replace("@", "")}`)
                      }
                      isSelected={isSelected}
                      onToggleCompare={handleToggleCompare}
                      canAddMore={canAddMore}
                    />
                  )}

                  {hasMore && (
                    <div className="flex justify-center mt-8">
                      <Button
                        onClick={handleLoadMore}
                        disabled={isLoadingMore || isFetching}
                        variant="outline"
                        size="lg"
                        className="min-w-[200px]"
                      >
                        {isLoadingMore || isFetching ? (
                          <LoadingSpinner size="small" label="Loading..." />
                        ) : (
                          `Load More (${totalCount - allInfluencers.length} remaining)`
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <CompareTray />
        <CompareModal />
      </div>
    </div>
  );
}
